import React, { useState } from 'react';
import {
    PointOfSale,
    People,
    Link,
    Receipt,
    Business,
    QrCodeScanner,
    Close,
    Menu,
    ChevronLeft,
    SwapHoriz,
    Dashboard
} from '@mui/icons-material';

const CaissierSidebar = ({ activeTab, onTabChange, caissierData, terminal }) => {
    const [showOrganizationsModal, setShowOrganizationsModal] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [organizations] = useState([
        {
            id: 1,
            name: 'Boutique Élégance',
            type: 'Commerce de détail',
            status: 'active',
            users: 5,
            lastActive: '2024-01-15'
        },
        {
            id: 2,
            name: 'Restaurant Le Gourmet',
            type: 'Restauration',
            status: 'active',
            users: 8,
            lastActive: '2024-01-14'
        },
        {
            id: 3,
            name: 'Tech Solutions SARL',
            type: 'Services IT',
            status: 'inactive',
            users: 3,
            lastActive: '2024-01-10'
        },
        {
            id: 4,
            name: 'Super Marché Central',
            type: 'Supermarché',
            status: 'active',
            users: 12,
            lastActive: '2024-01-15'
        }
    ]);

    const menuItems = [
        {
            id: 'dashboard',
            label: 'Tableau de bord',
            icon: Dashboard,
            description: 'Vue d\'ensemble',
            color: 'from-amber-500 to-orange-500'
        },
        {
            id: 'payment',
            label: 'Demander Paiement',
            icon: PointOfSale,
            description: 'Créer une transaction',
            color: 'from-blue-500 to-cyan-500'
        },
        {
            id: 'link',
            label: 'Lien de Paiement',
            icon: Link,
            description: 'Générer un lien',
            color: 'from-purple-500 to-purple-600'
        },
        // Factures / Articles masqués pour la mise en dev : décommenter pour réactiver
        // {
            // id: 'invoice',
            // label: 'Factures',
            // icon: Receipt,
            // description: 'Gérer les factures',
            // color: 'from-orange-500 to-amber-500'
        // },
        {
            id: 'transactions',
            label: 'Transactions',
            icon: SwapHoriz,
            description: 'Historique des transactions',
            color: 'from-green-500 to-emerald-500'
        },
        {
            id: 'clients',
            label: 'Liste Clients',
            icon: People,
            description: 'Gérer les clients',
            color: 'from-indigo-500 to-blue-500'
        },
    ];

    const handleOrganizationsClick = () => {
        setShowOrganizationsModal(true);
    };

    const handleConnectToOrganization = (orgId) => {
        console.log(`Connexion à l'organisation ${orgId}`);
        setShowOrganizationsModal(false);
    };

    const toggleSidebar = () => {
        setIsCollapsed(!isCollapsed);
    };

    const getStatusColor = (status) => {
        return status === 'active' ? 'bg-green-500' : 'bg-gray-500';
    };

    const getStatusText = (status) => {
        return status === 'active' ? 'Active' : 'Inactive';
    };

    // Composant ModalWrapper
    const ModalWrapper = ({ children, footer, title, subtitle, icon: Icon, onClose, size = 'md' }) => (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4 backdrop-blur-sm">
            <div
                className={`flex w-full min-h-0 flex-col ${
                    size === 'sm'
                        ? 'max-w-md'
                        : size === 'md'
                        ? 'max-w-2xl'
                        : size === 'lg'
                        ? 'max-w-4xl'
                        : 'max-w-6xl'
                } max-h-[min(90vh,900px)]`}
            >
                <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl border border-gray-700 bg-gradient-to-br from-gray-900 to-gray-800 shadow-2xl">
                    <div className="flex flex-shrink-0 items-center justify-between border-b border-gray-700 p-6">
                        <div className="flex items-center space-x-3">
                            <Icon className="text-2xl text-amber-400" />
                            <div>
                                <h2 className="text-xl font-bold text-white">{title}</h2>
                                <p className="text-sm text-gray-400">{subtitle}</p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg p-2 text-gray-400 transition-all duration-300 hover:bg-gray-700 hover:text-white"
                            aria-label="Fermer"
                        >
                            <Close className="text-xl" />
                        </button>
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">{children}</div>
                    {footer && (
                        <div className="flex-shrink-0 border-t border-gray-700 bg-gray-800/20">{footer}</div>
                    )}
                </div>
            </div>
        </div>
    );

    // Composant OrganizationCard
    const OrganizationCard = ({ org, onConnect }) => (
        <div className="bg-gray-800/50 rounded-xl p-4 border border-gray-700 hover:border-amber-500/30 transition-all duration-300 hover:bg-gray-700/30">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
                <div className="flex items-center space-x-4 flex-1">
                    <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
                        <Business className="text-white text-lg" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-white text-lg truncate">{org.name}</h3>
                        <p className="text-gray-400 text-sm">{org.type}</p>
                        <div className="flex flex-wrap items-center gap-3 mt-2">
                            <div className="flex items-center space-x-1">
                                <div className={`w-2 h-2 rounded-full ${getStatusColor(org.status)}`}></div>
                                <span className="text-xs text-gray-400">
                                    {getStatusText(org.status)}
                                </span>
                            </div>
                            <span className="text-xs text-gray-400">
                                {org.users} utilisateur(s)
                            </span>
                            <span className="text-xs text-gray-400">
                                Dernière activité: {org.lastActive}
                            </span>
                        </div>
                    </div>
                </div>
                <button
                    onClick={() => onConnect(org.id)}
                    disabled={org.status === 'inactive'}
                    className={`px-4 py-2 rounded-lg font-medium transition-all duration-300 whitespace-nowrap ${
                        org.status === 'active'
                            ? 'bg-amber-500 hover:bg-amber-600 text-white shadow-lg hover:shadow-amber-500/25'
                            : 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    }`}
                >
                    {org.status === 'active' ? 'Se connecter' : 'Inactive'}
                </button>
            </div>
        </div>
    );

    return (
        <>
            {/* Bouton pour ouvrir/fermer la sidebar (visible quand repliée) */}
            {isCollapsed && (
                <button
                    onClick={toggleSidebar}
                    className="fixed top-4 left-4 z-50 w-10 h-10 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center text-white shadow-lg hover:shadow-amber-500/25 transition-all duration-300 hover:scale-110"
                >
                    <Menu className="text-xl" />
                </button>
            )}

            <aside className={`fixed left-0 top-0 h-screen bg-gradient-to-b from-gray-900 to-gray-800 text-white shadow-2xl z-40 transition-all duration-300 ${
                isCollapsed ? 'w-20' : 'w-80'
            }`}>

                {/* En-tête Sidebar */}
                <div className="p-4 border-b border-gray-700">
                    <div className="flex items-center justify-between">
                        {!isCollapsed && (
                            <div className="flex items-center space-x-3 mb-4">
                                <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center">
                                    <QrCodeScanner className="text-white text-xl" />
                                </div>
                                <div>
                                    <h1 className="text-xl font-bold">Flow POS</h1>
                                    <p className="text-amber-200 text-sm">Interface Caissier</p>
                                </div>
                            </div>
                        )}

                        {isCollapsed && (
                            <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-amber-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                                <QrCodeScanner className="text-white text-xl" />
                            </div>
                        )}

                        {/* Bouton pour replier/déplier */}
                        <button
                            onClick={toggleSidebar}
                            className={`text-gray-400 hover:text-white transition-colors p-2 hover:bg-gray-700 rounded-lg ${
                                isCollapsed ? 'absolute bottom-4 left-1/2 transform -translate-x-1/2' : ''
                            }`}
                        >
                            <ChevronLeft className={`transition-transform duration-300 ${
                                isCollapsed ? 'rotate-180' : ''
                            }`} />
                        </button>
                    </div>

                    {/* Bouton Mes Organisations */}
                    {!isCollapsed && (
                        <button
                            onClick={handleOrganizationsClick}
                            className="w-full flex items-center justify-between p-3 bg-gray-800/50 rounded-xl hover:bg-gray-700/50 transition-colors group"
                        >
                            <div className="flex items-center space-x-3">
                                <div className="relative">
                                    <Business className="text-amber-400 group-hover:text-amber-300" />
                                </div>
                                <span className="font-medium">Mes Organisations</span>
                            </div>
                            <div className="text-amber-400 group-hover:text-amber-300 text-sm">
                                {organizations.length}
                            </div>
                        </button>
                    )}

                    {isCollapsed && (
                        <button
                            onClick={handleOrganizationsClick}
                            className="w-full flex items-center justify-center p-3 bg-gray-800/50 rounded-xl hover:bg-gray-700/50 transition-colors group relative"
                            title="Mes Organisations"
                        >
                            <Business className="text-amber-400 group-hover:text-amber-300" />
                            {organizations.length > 0 && (
                                <span className="absolute -top-1 -right-1 bg-amber-500 text-white text-xs rounded-full w-4 h-4 flex items-center justify-center">
                                    {organizations.length}
                                </span>
                            )}
                        </button>
                    )}
                </div>

                {/* Navigation */}
                <nav className="p-2 space-y-2">
                    {menuItems.map((item) => {
                        const IconComponent = item.icon;
                        const isActive = activeTab === item.id;

                        return (
                            <button
                                key={item.id}
                                onClick={() => onTabChange(item.id)}
                                className={`w-full text-left transition-all duration-300 group ${
                                    isCollapsed ? 'p-3 rounded-xl' : 'p-4 rounded-2xl'
                                } ${
                                    isActive
                                        ? 'bg-gradient-to-r from-amber-500/20 to-amber-600/20 border border-amber-500/30 shadow-lg'
                                        : 'hover:bg-gray-700/50 border border-transparent'
                                }`}
                                title={isCollapsed ? item.label : ''}
                            >
                                <div className={`flex items-center ${
                                    isCollapsed ? 'justify-center' : 'space-x-4'
                                }`}>
                                    <div className={`rounded-xl flex items-center justify-center bg-gradient-to-r ${item.color} shadow-md group-hover:scale-110 transition-transform ${
                                        isCollapsed ? 'w-10 h-10' : 'w-12 h-12'
                                    }`}>
                                        <IconComponent className="text-white text-xl" />
                                    </div>

                                    {!isCollapsed && (
                                        <div className="flex-1">
                                            <div className="font-semibold text-white group-hover:text-amber-200 transition-colors">
                                                {item.label}
                                            </div>
                                            <div className="text-gray-400 text-sm group-hover:text-gray-300 transition-colors">
                                                {item.description}
                                            </div>
                                        </div>
                                    )}

                                    {!isCollapsed && isActive && (
                                        <div className="w-2 h-2 bg-amber-400 rounded-full animate-pulse"></div>
                                    )}
                                </div>
                            </button>
                        );
                    })}
                </nav>

                {/* Section inférieure pour la version repliée */}
                {isCollapsed && (
                    <div className="absolute bottom-4 left-0 right-0 px-2">
                        <div className="text-center text-gray-500 text-xs">
                            © Flow POS
                        </div>
                    </div>
                )}
            </aside>

            {/* Overlay pour mobile */}
            {!isCollapsed && (
                <div
                    className="fixed inset-0 bg-black/50 z-30 lg:hidden"
                    onClick={() => setIsCollapsed(true)}
                />
            )}

            {/* Modal des Organisations */}
            {showOrganizationsModal && (
                <ModalWrapper
                    title="Mes Organisations"
                    subtitle="Sélectionnez une organisation pour vous connecter"
                    icon={Business}
                    onClose={() => setShowOrganizationsModal(false)}
                    size="md"
                    footer={
                        <div className="flex flex-col items-center justify-between gap-3 p-4 sm:flex-row">
                            <span className="text-center text-sm text-gray-400 sm:text-left">
                                {organizations.filter((org) => org.status === 'active').length} organisation(s)
                                active(s)
                            </span>
                            <button
                                type="button"
                                onClick={() => setShowOrganizationsModal(false)}
                                className="rounded-lg bg-gray-700/50 px-4 py-2 text-sm text-gray-400 transition-colors hover:bg-gray-700 hover:text-white"
                            >
                                Fermer
                            </button>
                        </div>
                    }
                >
                    <div className="p-6">
                        <div className="space-y-4 pb-2">
                            {organizations.map((org) => (
                                <OrganizationCard
                                    key={org.id}
                                    org={org}
                                    onConnect={handleConnectToOrganization}
                                />
                            ))}
                        </div>

                        {organizations.length === 0 && (
                            <div className="text-center py-8">
                                <Business className="text-gray-500 text-4xl mx-auto mb-4" />
                                <h3 className="text-gray-400 text-lg font-medium">Aucune organisation</h3>
                                <p className="text-gray-500 text-sm mt-1">
                                    Vous n'avez accès à aucune organisation pour le moment.
                                </p>
                            </div>
                        )}
                    </div>
                </ModalWrapper>
            )}
        </>
    );
};

export default CaissierSidebar;